import type { SubServiceDetail } from '../types';

export const subservicesEn: SubServiceDetail[] = [
  {
    "categorySlug": "video-produksiyon",
    "categoryTitle": "Video & Film Production",
    "slug": "tanitim-filmi",
    "title": "Corporate & Factory Promotional Films",
    "metaTitle": "Industrial & Factory Commercial Films - WE24 Agency",
    "metaDescription": "Cinematic factory commercial films for heavy industry and global exporters with 4K/6K cameras, FPV drone cinematography and multilingual mastering.",
    "keywords": [
      "factory promotional film",
      "corporate video production istanbul",
      "industrial commercial film",
      "manufacturing brand video"
    ],
    "heroTagline": "Elevate your manufacturing power and technological vision to global markets with cinematic storytelling.",
    "shortDescription": "We produce prestigious corporate promotional films for industrial facilities, technology hubs and global brands, capturing manufacturing precision, engineering excellence and global vision.",
    "fullDescription": "With over 15 years of specialized industrial production experience, WE24 Agency creates bespoke commercial and promotional films for leading global manufacturers and tech enterprises. From heavy industrial plants to high-tech cleanrooms, we transform the energy of your facilities into an inspiring visual story using RED and Sony cinema cameras, interior FPV drone flights, and anamorphic prime lenses.",
    "whyUs": [
      "15+ Years of Specialized Industrial Set and Factory Production Experience",
      "In-House Cinema Equipment (RED 6K, Sony FX Series, Anamorphic Lenses)",
      "Certified Commercial Drone Pilots for Indoor & Outdoor FPV Filming",
      "Multilingual Native Voiceover & International Subtitle Mastering",
      "Industrial Color Grading & Mastering in DaVinci Resolve"
    ],
    "deliverables": [
      "Main Promotional Master Film (3:30 - 5:00 min 4K RAW Master)",
      "Expo & Summit Edition (1-min High-Energy Teaser)",
      "Social Media & LinkedIn Edits (9:16 and 1:1 Formats)",
      "High-Resolution Industrial & Facility Photo Archive",
      "Multilingual Master Video Packages (EN, DE, FR, TR)"
    ],
    "process": [
      "Site Scouting & Technical Script: We explore your plant, analyze critical assembly lines and craft an engaging narrative flow.",
      "Safety Protocols & Pre-Production: Detailed scheduling in full compliance with occupational health and safety standards.",
      "Cinematic Filming Days: Multi-day filming with directors, DPs, gimbal operators and licensed drone pilots.",
      "Post-Production & Color: Precision editing, dynamic sound design, DaVinci Resolve color grading and 3D infographics.",
      "Master Delivery: Delivering high-bitrate masters tailored for trade shows, investor pitches and digital campaigns."
    ],
    "faqs": [
      {
        "question": "Does industrial filming disrupt ongoing manufacturing operations?",
        "answer": "No. Our production crew adheres to strict OHS safety protocols and plans all camera setups to avoid interfering with active production lines."
      },
      {
        "question": "Can drones fly inside enclosed factory environments?",
        "answer": "Yes. Our certified pilots utilize prop-guarded micro FPV drones designed safely for indoor industrial navigation."
      },
      {
        "question": "How many languages can the video be localized into?",
        "answer": "We provide native voiceover and subtitling in English, German, French, Arabic, Spanish and other requested languages."
      }
    ]
  },
  {
    "categorySlug": "video-produksiyon",
    "categoryTitle": "Video & Film Production",
    "slug": "reklam-filmi",
    "title": "Television & Digital Commercial Film Production",
    "metaTitle": "Commercial & Digital Ad Video Production - WE24 Agency",
    "metaDescription": "Creative TV commercials, digital video ads, high-converting brand campaigns and multi-channel video production.",
    "keywords": [
      "commercial film production",
      "tv ad agency istanbul",
      "digital video advertising",
      "brand commercial video"
    ],
    "heroTagline": "Drive high brand recall and consumer conversion with high-concept commercial films.",
    "shortDescription": "We direct and produce high-concept TV commercials and digital video ads designed to captivate audiences within the first 3 seconds.",
    "fullDescription": "WE24 Agency delivers end-to-end commercial film productions that merge compelling creative concepts with broadcast-standard cinematography. From professional casting and custom set design to dynamic visual effects and high-impact sound design, we craft advertising films that elevate brand prestige.",
    "whyUs": [
      "Creative Concepts Engineered for Maximum Brand Recall",
      "Broadcast-Grade TVC Standards (EBU R128 Audio & DCI-P3 Color)",
      "Full In-House Crew: Directors, Cinematographers & Art Department",
      "Multi-Format Deliverables for TV, YouTube, Meta and TikTok",
      "Fast-Track Post-Production & Color Mastering"
    ],
    "deliverables": [
      "Master Commercial Film (45s, 30s, 15s TVC & Digital Cuts)",
      "6-Second Bumper Ads & Vertical 9:16 Variations",
      "Key Visuals & High-Resolution Campaign Stills Pack",
      "Clean Music & Sound Effects Split Audio Stems"
    ],
    "process": [
      "Creative Concept & Scriptwriting: Developing unique concepts, storyboards and copy tailored to your brand archetype.",
      "Casting & Pre-Production: Professional actor casting, wardrobe styling and location scouting.",
      "Commercial Production Days: High-end filming with cinema cameras, specialized studio lighting and motion control rigs.",
      "Post-Production & Polish: Rhythmic editing, photorealistic CGI/VFX, color grading and original music scoring.",
      "Multi-Channel Delivery: Broadcast-ready and platform-optimized video files."
    ],
    "faqs": [
      {
        "question": "What is the typical timeframe for a commercial production?",
        "answer": "A standard commercial production takes 3 to 5 weeks from initial storyboard to final broadcast delivery."
      },
      {
        "question": "Do you deliver broadcast-ready TVC files?",
        "answer": "Yes, our deliverables meet all international TV broadcast technical specifications."
      }
    ]
  },
  {
    "categorySlug": "video-produksiyon",
    "categoryTitle": "Video & Film Production",
    "slug": "muzik-video-klip",
    "title": "Music Video & Performance Production",
    "metaTitle": "Music Video & Artist Performance Production - WE24 Agency",
    "metaDescription": "Stylized, artistic and viral music video productions with innovative art direction, dynamic lighting and rhythmic editing.",
    "keywords": [
      "music video production",
      "music video director istanbul",
      "artist clip filming",
      "cinematic music video"
    ],
    "heroTagline": "Amplify your music with bold visual concepts, striking art direction and cinematic rhythm.",
    "shortDescription": "We produce visually captivating music videos that define an artist's persona with avant-garde lighting, stylish set design and rhythmic montage.",
    "fullDescription": "From chart-topping pop singles to cinematic indie releases, WE24 Agency directs and produces unforgettable music videos. We combine anamorphic optics, synchronized dynamic lighting systems and trendsetting visual aesthetics to bring songs to life.",
    "whyUs": [
      "Avant-Garde Art Direction & Custom Lighting Setups",
      "Anamorphic Cinema Primes & High-Speed Slow-Motion Capture",
      "Choreography & Multi-Set Production Management",
      "Precision Audio-Sync Editing & Stylized Film-Look Grading",
      "Viral Vertical Teaser Packages for TikTok & Instagram"
    ],
    "deliverables": [
      "Official 4K Music Video Master",
      "Vertical Teasers & Behind-The-Scenes Video Package",
      "Single Cover Artworks & Editorial Photo Archive",
      "Apple Music & Spotify Canvas Loops"
    ],
    "process": [
      "Audio Analysis & Concept: Translating melody, tempo and lyrics into a compelling visual narrative.",
      "Set Design & Styling: Designing customized sets, lighting atmospheres and artist wardrobe.",
      "Set Production: Directing performance and narrative scenes with cinematic cameras.",
      "Post-Production: Sync cutting, rhythm balancing, VFX touches and film-emulation color grading."
    ],
    "faqs": [
      {
        "question": "Do you handle location permits and studio bookings?",
        "answer": "Yes, our production team arranges all location scouting, studio rentals and filming permits."
      }
    ]
  },
  {
    "categorySlug": "video-produksiyon",
    "categoryTitle": "Video & Film Production",
    "slug": "havadan-drone-video",
    "title": "Lisanslı Havadan Drone Video ve Fotoğraf Çekimi",
    "metaTitle": "Lisanslı Havadan Drone Video ve Fotoğraf Çekimi - WE24 Agency",
    "metaDescription": "Professional lisanslı havadan drone video ve fotoğraf çekimi services by WE24 Agency. Delivering high-standard creative, production and technological solutions.",
    "keywords": [
      "lisanslı havadan drone video ve fotoğraf çekimi",
      "we24 agency istanbul",
      "creative agency",
      "production services"
    ],
    "heroTagline": "Accelerate your brand with world-class lisanslı havadan drone video ve fotoğraf çekimi solutions.",
    "shortDescription": "We provide comprehensive, high-standard lisanslı havadan drone video ve fotoğraf çekimi services tailored to elevate brand prestige and drive measurable results.",
    "fullDescription": "WE24 Agency delivers premium lisanslı havadan drone video ve fotoğraf çekimi services powered by deep industry experience, cutting-edge technology and refined aesthetic craft. We partner with leading global and national brands to create impactful, lasting digital and creative assets.",
    "whyUs": [
      "15+ Years of Specialized Industry Experience",
      "End-to-End In-House Production & Engineering Teams",
      "State-of-the-Art Technical Equipment & Software Stacks",
      "Multilingual Global Delivery Standards",
      "Dedicated Project Management & Agile Communication"
    ],
    "deliverables": [
      "High-Resolution Master Deliverables (4K / Lossless / Vector)",
      "Platform-Optimized Multi-Format Digital Assets",
      "Full Source Files, Brand Guidelines & Documentation",
      "Dedicated Technical Support & Maintenance"
    ],
    "process": [
      "Discovery & Strategy: In-depth analysis of your objectives, audience and technical requirements.",
      "Creative & Technical Execution: Precision production and development by our senior specialists.",
      "Review & Quality Assurance: Rigorous testing, multi-device audits and client feedback iterations.",
      "Final Master Delivery: Seamless handover of all production assets, source codes and master files."
    ],
    "faqs": [
      {
        "question": "How does the Lisanslı Havadan Drone Video ve Fotoğraf Çekimi project workflow begin?",
        "answer": "We begin with an in-depth discovery consultation to align on your vision, followed by a detailed scope and delivery timeline proposal."
      },
      {
        "question": "What is the typical delivery timeline?",
        "answer": "Project timelines typically range from 1 to 3 weeks depending on the exact scope, revision rounds and technical requirements."
      },
      {
        "question": "Do we retain full intellectual property ownership?",
        "answer": "Yes, 100% of the final deliverables, master files and intellectual property belong exclusively to your brand."
      }
    ]
  },
  {
    "categorySlug": "video-produksiyon",
    "categoryTitle": "Video & Film Production",
    "slug": "360-vr-video",
    "title": "360° VR & Immersive Video Production",
    "metaTitle": "360° VR & Spatial Video Production - WE24 Agency",
    "metaDescription": "Ultra high-resolution 360-degree VR videos and spatial walkthroughs for real estate, tourism, virtual showrooms and industrial plants.",
    "keywords": [
      "360 degree video production",
      "virtual reality tour",
      "8k vr video",
      "spatial video istanbul"
    ],
    "heroTagline": "Transport your global audiences into interactive 8K virtual reality experiences.",
    "shortDescription": "We produce interactive 8K 360-degree virtual reality tours and spatial experiences for VR headsets, web and mobile devices.",
    "fullDescription": "Allow clients worldwide to walk through your factory, showroom or luxury property in photorealistic 8K virtual reality. We record spatial ambisonic audio and stitch flawless multi-lens panoramas for web and VR headsets.",
    "whyUs": [
      "8K Ultra-Sharp Spherical Video Resolution",
      "Spatial Ambisonic 3D Audio Recording",
      "Lightweight Web Player Integration (No App Required)",
      "Interactive Hotspots, Video Links & Information Cards",
      "Compatible with Meta Quest, Apple Vision Pro and Mobile Browsers"
    ],
    "deliverables": [
      "8K 360° Video Master Files",
      "Interactive Web Embed Code & VR Tour Portal",
      "Spatial VR Headset Optimized Packages"
    ],
    "process": [
      "Trajectory Planning: Mapping optimal nodal points and walkthrough routes.",
      "8K Spherical Capture: Filming distortion-free 360° scenes with spatial sound.",
      "Stitching & Web Authoring: Stitching multi-camera plates and embedding interactive hotspots."
    ],
    "faqs": [
      {
        "question": "Can 360° videos be embedded directly into our website?",
        "answer": "Yes, we provide lightweight web players that load quickly on any browser without plugins."
      }
    ]
  },
  {
    "categorySlug": "video-produksiyon",
    "categoryTitle": "Video & Film Production",
    "slug": "etkinlik-aftermovie",
    "title": "Etkinlik, Zirve ve Lansman Aftermovie Filmleri",
    "metaTitle": "Etkinlik, Zirve ve Lansman Aftermovie Filmleri - WE24 Agency",
    "metaDescription": "Professional etkinlik, zirve ve lansman aftermovie filmleri services by WE24 Agency. Delivering high-standard creative, production and technological solutions.",
    "keywords": [
      "etkinlik, zirve ve lansman aftermovie filmleri",
      "we24 agency istanbul",
      "creative agency",
      "production services"
    ],
    "heroTagline": "Accelerate your brand with world-class etkinlik, zirve ve lansman aftermovie filmleri solutions.",
    "shortDescription": "We provide comprehensive, high-standard etkinlik, zirve ve lansman aftermovie filmleri services tailored to elevate brand prestige and drive measurable results.",
    "fullDescription": "WE24 Agency delivers premium etkinlik, zirve ve lansman aftermovie filmleri services powered by deep industry experience, cutting-edge technology and refined aesthetic craft. We partner with leading global and national brands to create impactful, lasting digital and creative assets.",
    "whyUs": [
      "15+ Years of Specialized Industry Experience",
      "End-to-End In-House Production & Engineering Teams",
      "State-of-the-Art Technical Equipment & Software Stacks",
      "Multilingual Global Delivery Standards",
      "Dedicated Project Management & Agile Communication"
    ],
    "deliverables": [
      "High-Resolution Master Deliverables (4K / Lossless / Vector)",
      "Platform-Optimized Multi-Format Digital Assets",
      "Full Source Files, Brand Guidelines & Documentation",
      "Dedicated Technical Support & Maintenance"
    ],
    "process": [
      "Discovery & Strategy: In-depth analysis of your objectives, audience and technical requirements.",
      "Creative & Technical Execution: Precision production and development by our senior specialists.",
      "Review & Quality Assurance: Rigorous testing, multi-device audits and client feedback iterations.",
      "Final Master Delivery: Seamless handover of all production assets, source codes and master files."
    ],
    "faqs": [
      {
        "question": "How does the Etkinlik, Zirve ve Lansman Aftermovie Filmleri project workflow begin?",
        "answer": "We begin with an in-depth discovery consultation to align on your vision, followed by a detailed scope and delivery timeline proposal."
      },
      {
        "question": "What is the typical delivery timeline?",
        "answer": "Project timelines typically range from 1 to 3 weeks depending on the exact scope, revision rounds and technical requirements."
      },
      {
        "question": "Do we retain full intellectual property ownership?",
        "answer": "Yes, 100% of the final deliverables, master files and intellectual property belong exclusively to your brand."
      }
    ]
  },
  {
    "categorySlug": "canli-yayin-program",
    "categoryTitle": "Live Broadcasting & Direction",
    "slug": "reji-canli-yayin",
    "title": "Multi-Camera Live Broadcast Direction",
    "metaTitle": "Multi-Camera Live Broadcast Direction - WE24 Agency",
    "metaDescription": "Professional multi-camera live broadcast direction services by WE24 Agency. Delivering high-standard creative, production and technological solutions.",
    "keywords": [
      "multi-camera live broadcast direction",
      "we24 agency istanbul",
      "creative agency",
      "production services"
    ],
    "heroTagline": "Accelerate your brand with world-class multi-camera live broadcast direction solutions.",
    "shortDescription": "We provide comprehensive, high-standard multi-camera live broadcast direction services tailored to elevate brand prestige and drive measurable results.",
    "fullDescription": "WE24 Agency delivers premium multi-camera live broadcast direction services powered by deep industry experience, cutting-edge technology and refined aesthetic craft. We partner with leading global and national brands to create impactful, lasting digital and creative assets.",
    "whyUs": [
      "15+ Years of Specialized Industry Experience",
      "End-to-End In-House Production & Engineering Teams",
      "State-of-the-Art Technical Equipment & Software Stacks",
      "Multilingual Global Delivery Standards",
      "Dedicated Project Management & Agile Communication"
    ],
    "deliverables": [
      "High-Resolution Master Deliverables (4K / Lossless / Vector)",
      "Platform-Optimized Multi-Format Digital Assets",
      "Full Source Files, Brand Guidelines & Documentation",
      "Dedicated Technical Support & Maintenance"
    ],
    "process": [
      "Discovery & Strategy: In-depth analysis of your objectives, audience and technical requirements.",
      "Creative & Technical Execution: Precision production and development by our senior specialists.",
      "Review & Quality Assurance: Rigorous testing, multi-device audits and client feedback iterations.",
      "Final Master Delivery: Seamless handover of all production assets, source codes and master files."
    ],
    "faqs": [
      {
        "question": "How does the Multi-Camera Live Broadcast Direction project workflow begin?",
        "answer": "We begin with an in-depth discovery consultation to align on your vision, followed by a detailed scope and delivery timeline proposal."
      },
      {
        "question": "What is the typical delivery timeline?",
        "answer": "Project timelines typically range from 1 to 3 weeks depending on the exact scope, revision rounds and technical requirements."
      },
      {
        "question": "Do we retain full intellectual property ownership?",
        "answer": "Yes, 100% of the final deliverables, master files and intellectual property belong exclusively to your brand."
      }
    ]
  },
  {
    "categorySlug": "canli-yayin-program",
    "categoryTitle": "Live Broadcasting & Direction",
    "slug": "espor-gaming-yayin",
    "title": "Esports & Gaming Tournament Streaming",
    "metaTitle": "Esports & Gaming Tournament Streaming - WE24 Agency",
    "metaDescription": "Professional esports & gaming tournament streaming services by WE24 Agency. Delivering high-standard creative, production and technological solutions.",
    "keywords": [
      "esports & gaming tournament streaming",
      "we24 agency istanbul",
      "creative agency",
      "production services"
    ],
    "heroTagline": "Accelerate your brand with world-class esports & gaming tournament streaming solutions.",
    "shortDescription": "We provide comprehensive, high-standard esports & gaming tournament streaming services tailored to elevate brand prestige and drive measurable results.",
    "fullDescription": "WE24 Agency delivers premium esports & gaming tournament streaming services powered by deep industry experience, cutting-edge technology and refined aesthetic craft. We partner with leading global and national brands to create impactful, lasting digital and creative assets.",
    "whyUs": [
      "15+ Years of Specialized Industry Experience",
      "End-to-End In-House Production & Engineering Teams",
      "State-of-the-Art Technical Equipment & Software Stacks",
      "Multilingual Global Delivery Standards",
      "Dedicated Project Management & Agile Communication"
    ],
    "deliverables": [
      "High-Resolution Master Deliverables (4K / Lossless / Vector)",
      "Platform-Optimized Multi-Format Digital Assets",
      "Full Source Files, Brand Guidelines & Documentation",
      "Dedicated Technical Support & Maintenance"
    ],
    "process": [
      "Discovery & Strategy: In-depth analysis of your objectives, audience and technical requirements.",
      "Creative & Technical Execution: Precision production and development by our senior specialists.",
      "Review & Quality Assurance: Rigorous testing, multi-device audits and client feedback iterations.",
      "Final Master Delivery: Seamless handover of all production assets, source codes and master files."
    ],
    "faqs": [
      {
        "question": "How does the Esports & Gaming Tournament Streaming project workflow begin?",
        "answer": "We begin with an in-depth discovery consultation to align on your vision, followed by a detailed scope and delivery timeline proposal."
      },
      {
        "question": "What is the typical delivery timeline?",
        "answer": "Project timelines typically range from 1 to 3 weeks depending on the exact scope, revision rounds and technical requirements."
      },
      {
        "question": "Do we retain full intellectual property ownership?",
        "answer": "Yes, 100% of the final deliverables, master files and intellectual property belong exclusively to your brand."
      }
    ]
  },
  {
    "categorySlug": "canli-yayin-program",
    "categoryTitle": "Live Broadcasting & Direction",
    "slug": "hibrit-etkinlik-zirve",
    "title": "Hybrid Events & International Summits",
    "metaTitle": "Hybrid Events & International Summits - WE24 Agency",
    "metaDescription": "Professional hybrid events & international summits services by WE24 Agency. Delivering high-standard creative, production and technological solutions.",
    "keywords": [
      "hybrid events & international summits",
      "we24 agency istanbul",
      "creative agency",
      "production services"
    ],
    "heroTagline": "Accelerate your brand with world-class hybrid events & international summits solutions.",
    "shortDescription": "We provide comprehensive, high-standard hybrid events & international summits services tailored to elevate brand prestige and drive measurable results.",
    "fullDescription": "WE24 Agency delivers premium hybrid events & international summits services powered by deep industry experience, cutting-edge technology and refined aesthetic craft. We partner with leading global and national brands to create impactful, lasting digital and creative assets.",
    "whyUs": [
      "15+ Years of Specialized Industry Experience",
      "End-to-End In-House Production & Engineering Teams",
      "State-of-the-Art Technical Equipment & Software Stacks",
      "Multilingual Global Delivery Standards",
      "Dedicated Project Management & Agile Communication"
    ],
    "deliverables": [
      "High-Resolution Master Deliverables (4K / Lossless / Vector)",
      "Platform-Optimized Multi-Format Digital Assets",
      "Full Source Files, Brand Guidelines & Documentation",
      "Dedicated Technical Support & Maintenance"
    ],
    "process": [
      "Discovery & Strategy: In-depth analysis of your objectives, audience and technical requirements.",
      "Creative & Technical Execution: Precision production and development by our senior specialists.",
      "Review & Quality Assurance: Rigorous testing, multi-device audits and client feedback iterations.",
      "Final Master Delivery: Seamless handover of all production assets, source codes and master files."
    ],
    "faqs": [
      {
        "question": "How does the Hybrid Events & International Summits project workflow begin?",
        "answer": "We begin with an in-depth discovery consultation to align on your vision, followed by a detailed scope and delivery timeline proposal."
      },
      {
        "question": "What is the typical delivery timeline?",
        "answer": "Project timelines typically range from 1 to 3 weeks depending on the exact scope, revision rounds and technical requirements."
      },
      {
        "question": "Do we retain full intellectual property ownership?",
        "answer": "Yes, 100% of the final deliverables, master files and intellectual property belong exclusively to your brand."
      }
    ]
  },
  {
    "categorySlug": "canli-yayin-program",
    "categoryTitle": "Live Broadcasting & Direction",
    "slug": "simulcast-yayin",
    "title": "Multi-Platform Simulcast Streaming",
    "metaTitle": "Multi-Platform Simulcast Streaming - WE24 Agency",
    "metaDescription": "Professional multi-platform simulcast streaming services by WE24 Agency. Delivering high-standard creative, production and technological solutions.",
    "keywords": [
      "multi-platform simulcast streaming",
      "we24 agency istanbul",
      "creative agency",
      "production services"
    ],
    "heroTagline": "Accelerate your brand with world-class multi-platform simulcast streaming solutions.",
    "shortDescription": "We provide comprehensive, high-standard multi-platform simulcast streaming services tailored to elevate brand prestige and drive measurable results.",
    "fullDescription": "WE24 Agency delivers premium multi-platform simulcast streaming services powered by deep industry experience, cutting-edge technology and refined aesthetic craft. We partner with leading global and national brands to create impactful, lasting digital and creative assets.",
    "whyUs": [
      "15+ Years of Specialized Industry Experience",
      "End-to-End In-House Production & Engineering Teams",
      "State-of-the-Art Technical Equipment & Software Stacks",
      "Multilingual Global Delivery Standards",
      "Dedicated Project Management & Agile Communication"
    ],
    "deliverables": [
      "High-Resolution Master Deliverables (4K / Lossless / Vector)",
      "Platform-Optimized Multi-Format Digital Assets",
      "Full Source Files, Brand Guidelines & Documentation",
      "Dedicated Technical Support & Maintenance"
    ],
    "process": [
      "Discovery & Strategy: In-depth analysis of your objectives, audience and technical requirements.",
      "Creative & Technical Execution: Precision production and development by our senior specialists.",
      "Review & Quality Assurance: Rigorous testing, multi-device audits and client feedback iterations.",
      "Final Master Delivery: Seamless handover of all production assets, source codes and master files."
    ],
    "faqs": [
      {
        "question": "How does the Multi-Platform Simulcast Streaming project workflow begin?",
        "answer": "We begin with an in-depth discovery consultation to align on your vision, followed by a detailed scope and delivery timeline proposal."
      },
      {
        "question": "What is the typical delivery timeline?",
        "answer": "Project timelines typically range from 1 to 3 weeks depending on the exact scope, revision rounds and technical requirements."
      },
      {
        "question": "Do we retain full intellectual property ownership?",
        "answer": "Yes, 100% of the final deliverables, master files and intellectual property belong exclusively to your brand."
      }
    ]
  },
  {
    "categorySlug": "canli-yayin-program",
    "categoryTitle": "Live Broadcasting & Direction",
    "slug": "studyo-programciligi",
    "title": "Studio Programming & Talk Show Production",
    "metaTitle": "Studio Programming & Talk Show Production - WE24 Agency",
    "metaDescription": "Professional studio programming & talk show production services by WE24 Agency. Delivering high-standard creative, production and technological solutions.",
    "keywords": [
      "studio programming & talk show production",
      "we24 agency istanbul",
      "creative agency",
      "production services"
    ],
    "heroTagline": "Accelerate your brand with world-class studio programming & talk show production solutions.",
    "shortDescription": "We provide comprehensive, high-standard studio programming & talk show production services tailored to elevate brand prestige and drive measurable results.",
    "fullDescription": "WE24 Agency delivers premium studio programming & talk show production services powered by deep industry experience, cutting-edge technology and refined aesthetic craft. We partner with leading global and national brands to create impactful, lasting digital and creative assets.",
    "whyUs": [
      "15+ Years of Specialized Industry Experience",
      "End-to-End In-House Production & Engineering Teams",
      "State-of-the-Art Technical Equipment & Software Stacks",
      "Multilingual Global Delivery Standards",
      "Dedicated Project Management & Agile Communication"
    ],
    "deliverables": [
      "High-Resolution Master Deliverables (4K / Lossless / Vector)",
      "Platform-Optimized Multi-Format Digital Assets",
      "Full Source Files, Brand Guidelines & Documentation",
      "Dedicated Technical Support & Maintenance"
    ],
    "process": [
      "Discovery & Strategy: In-depth analysis of your objectives, audience and technical requirements.",
      "Creative & Technical Execution: Precision production and development by our senior specialists.",
      "Review & Quality Assurance: Rigorous testing, multi-device audits and client feedback iterations.",
      "Final Master Delivery: Seamless handover of all production assets, source codes and master files."
    ],
    "faqs": [
      {
        "question": "How does the Studio Programming & Talk Show Production project workflow begin?",
        "answer": "We begin with an in-depth discovery consultation to align on your vision, followed by a detailed scope and delivery timeline proposal."
      },
      {
        "question": "What is the typical delivery timeline?",
        "answer": "Project timelines typically range from 1 to 3 weeks depending on the exact scope, revision rounds and technical requirements."
      },
      {
        "question": "Do we retain full intellectual property ownership?",
        "answer": "Yes, 100% of the final deliverables, master files and intellectual property belong exclusively to your brand."
      }
    ]
  },
  {
    "categorySlug": "post-produksiyon",
    "categoryTitle": "Post-Production & VFX",
    "slug": "kurgu-montaj",
    "title": "Professional Video Editing & Assembly",
    "metaTitle": "Professional Video Editing & Assembly - WE24 Agency",
    "metaDescription": "Professional professional video editing & assembly services by WE24 Agency. Delivering high-standard creative, production and technological solutions.",
    "keywords": [
      "professional video editing & assembly",
      "we24 agency istanbul",
      "creative agency",
      "production services"
    ],
    "heroTagline": "Accelerate your brand with world-class professional video editing & assembly solutions.",
    "shortDescription": "We provide comprehensive, high-standard professional video editing & assembly services tailored to elevate brand prestige and drive measurable results.",
    "fullDescription": "WE24 Agency delivers premium professional video editing & assembly services powered by deep industry experience, cutting-edge technology and refined aesthetic craft. We partner with leading global and national brands to create impactful, lasting digital and creative assets.",
    "whyUs": [
      "15+ Years of Specialized Industry Experience",
      "End-to-End In-House Production & Engineering Teams",
      "State-of-the-Art Technical Equipment & Software Stacks",
      "Multilingual Global Delivery Standards",
      "Dedicated Project Management & Agile Communication"
    ],
    "deliverables": [
      "High-Resolution Master Deliverables (4K / Lossless / Vector)",
      "Platform-Optimized Multi-Format Digital Assets",
      "Full Source Files, Brand Guidelines & Documentation",
      "Dedicated Technical Support & Maintenance"
    ],
    "process": [
      "Discovery & Strategy: In-depth analysis of your objectives, audience and technical requirements.",
      "Creative & Technical Execution: Precision production and development by our senior specialists.",
      "Review & Quality Assurance: Rigorous testing, multi-device audits and client feedback iterations.",
      "Final Master Delivery: Seamless handover of all production assets, source codes and master files."
    ],
    "faqs": [
      {
        "question": "How does the Professional Video Editing & Assembly project workflow begin?",
        "answer": "We begin with an in-depth discovery consultation to align on your vision, followed by a detailed scope and delivery timeline proposal."
      },
      {
        "question": "What is the typical delivery timeline?",
        "answer": "Project timelines typically range from 1 to 3 weeks depending on the exact scope, revision rounds and technical requirements."
      },
      {
        "question": "Do we retain full intellectual property ownership?",
        "answer": "Yes, 100% of the final deliverables, master files and intellectual property belong exclusively to your brand."
      }
    ]
  },
  {
    "categorySlug": "post-produksiyon",
    "categoryTitle": "Post-Production & VFX",
    "slug": "renk-duzenleme",
    "title": "DaVinci Resolve Color Grading",
    "metaTitle": "DaVinci Resolve Color Grading - WE24 Agency",
    "metaDescription": "Professional davinci resolve color grading services by WE24 Agency. Delivering high-standard creative, production and technological solutions.",
    "keywords": [
      "davinci resolve color grading",
      "we24 agency istanbul",
      "creative agency",
      "production services"
    ],
    "heroTagline": "Accelerate your brand with world-class davinci resolve color grading solutions.",
    "shortDescription": "We provide comprehensive, high-standard davinci resolve color grading services tailored to elevate brand prestige and drive measurable results.",
    "fullDescription": "WE24 Agency delivers premium davinci resolve color grading services powered by deep industry experience, cutting-edge technology and refined aesthetic craft. We partner with leading global and national brands to create impactful, lasting digital and creative assets.",
    "whyUs": [
      "15+ Years of Specialized Industry Experience",
      "End-to-End In-House Production & Engineering Teams",
      "State-of-the-Art Technical Equipment & Software Stacks",
      "Multilingual Global Delivery Standards",
      "Dedicated Project Management & Agile Communication"
    ],
    "deliverables": [
      "High-Resolution Master Deliverables (4K / Lossless / Vector)",
      "Platform-Optimized Multi-Format Digital Assets",
      "Full Source Files, Brand Guidelines & Documentation",
      "Dedicated Technical Support & Maintenance"
    ],
    "process": [
      "Discovery & Strategy: In-depth analysis of your objectives, audience and technical requirements.",
      "Creative & Technical Execution: Precision production and development by our senior specialists.",
      "Review & Quality Assurance: Rigorous testing, multi-device audits and client feedback iterations.",
      "Final Master Delivery: Seamless handover of all production assets, source codes and master files."
    ],
    "faqs": [
      {
        "question": "How does the DaVinci Resolve Color Grading project workflow begin?",
        "answer": "We begin with an in-depth discovery consultation to align on your vision, followed by a detailed scope and delivery timeline proposal."
      },
      {
        "question": "What is the typical delivery timeline?",
        "answer": "Project timelines typically range from 1 to 3 weeks depending on the exact scope, revision rounds and technical requirements."
      },
      {
        "question": "Do we retain full intellectual property ownership?",
        "answer": "Yes, 100% of the final deliverables, master files and intellectual property belong exclusively to your brand."
      }
    ]
  },
  {
    "categorySlug": "post-produksiyon",
    "categoryTitle": "Post-Production & VFX",
    "slug": "vfx-gorsel-efekt",
    "title": "VFX & Visual Effects Studio",
    "metaTitle": "VFX & Visual Effects Studio - WE24 Agency",
    "metaDescription": "Professional vfx & visual effects studio services by WE24 Agency. Delivering high-standard creative, production and technological solutions.",
    "keywords": [
      "vfx & visual effects studio",
      "we24 agency istanbul",
      "creative agency",
      "production services"
    ],
    "heroTagline": "Accelerate your brand with world-class vfx & visual effects studio solutions.",
    "shortDescription": "We provide comprehensive, high-standard vfx & visual effects studio services tailored to elevate brand prestige and drive measurable results.",
    "fullDescription": "WE24 Agency delivers premium vfx & visual effects studio services powered by deep industry experience, cutting-edge technology and refined aesthetic craft. We partner with leading global and national brands to create impactful, lasting digital and creative assets.",
    "whyUs": [
      "15+ Years of Specialized Industry Experience",
      "End-to-End In-House Production & Engineering Teams",
      "State-of-the-Art Technical Equipment & Software Stacks",
      "Multilingual Global Delivery Standards",
      "Dedicated Project Management & Agile Communication"
    ],
    "deliverables": [
      "High-Resolution Master Deliverables (4K / Lossless / Vector)",
      "Platform-Optimized Multi-Format Digital Assets",
      "Full Source Files, Brand Guidelines & Documentation",
      "Dedicated Technical Support & Maintenance"
    ],
    "process": [
      "Discovery & Strategy: In-depth analysis of your objectives, audience and technical requirements.",
      "Creative & Technical Execution: Precision production and development by our senior specialists.",
      "Review & Quality Assurance: Rigorous testing, multi-device audits and client feedback iterations.",
      "Final Master Delivery: Seamless handover of all production assets, source codes and master files."
    ],
    "faqs": [
      {
        "question": "How does the VFX & Visual Effects Studio project workflow begin?",
        "answer": "We begin with an in-depth discovery consultation to align on your vision, followed by a detailed scope and delivery timeline proposal."
      },
      {
        "question": "What is the typical delivery timeline?",
        "answer": "Project timelines typically range from 1 to 3 weeks depending on the exact scope, revision rounds and technical requirements."
      },
      {
        "question": "Do we retain full intellectual property ownership?",
        "answer": "Yes, 100% of the final deliverables, master files and intellectual property belong exclusively to your brand."
      }
    ]
  },
  {
    "categorySlug": "post-produksiyon",
    "categoryTitle": "Post-Production & VFX",
    "slug": "animasyon-motion-graphics",
    "title": "2D/3D Animation & Motion Design",
    "metaTitle": "2D/3D Animation & Motion Design - WE24 Agency",
    "metaDescription": "Professional 2d/3d animation & motion design services by WE24 Agency. Delivering high-standard creative, production and technological solutions.",
    "keywords": [
      "2d/3d animation & motion design",
      "we24 agency istanbul",
      "creative agency",
      "production services"
    ],
    "heroTagline": "Accelerate your brand with world-class 2d/3d animation & motion design solutions.",
    "shortDescription": "We provide comprehensive, high-standard 2d/3d animation & motion design services tailored to elevate brand prestige and drive measurable results.",
    "fullDescription": "WE24 Agency delivers premium 2d/3d animation & motion design services powered by deep industry experience, cutting-edge technology and refined aesthetic craft. We partner with leading global and national brands to create impactful, lasting digital and creative assets.",
    "whyUs": [
      "15+ Years of Specialized Industry Experience",
      "End-to-End In-House Production & Engineering Teams",
      "State-of-the-Art Technical Equipment & Software Stacks",
      "Multilingual Global Delivery Standards",
      "Dedicated Project Management & Agile Communication"
    ],
    "deliverables": [
      "High-Resolution Master Deliverables (4K / Lossless / Vector)",
      "Platform-Optimized Multi-Format Digital Assets",
      "Full Source Files, Brand Guidelines & Documentation",
      "Dedicated Technical Support & Maintenance"
    ],
    "process": [
      "Discovery & Strategy: In-depth analysis of your objectives, audience and technical requirements.",
      "Creative & Technical Execution: Precision production and development by our senior specialists.",
      "Review & Quality Assurance: Rigorous testing, multi-device audits and client feedback iterations.",
      "Final Master Delivery: Seamless handover of all production assets, source codes and master files."
    ],
    "faqs": [
      {
        "question": "How does the 2D/3D Animation & Motion Design project workflow begin?",
        "answer": "We begin with an in-depth discovery consultation to align on your vision, followed by a detailed scope and delivery timeline proposal."
      },
      {
        "question": "What is the typical delivery timeline?",
        "answer": "Project timelines typically range from 1 to 3 weeks depending on the exact scope, revision rounds and technical requirements."
      },
      {
        "question": "Do we retain full intellectual property ownership?",
        "answer": "Yes, 100% of the final deliverables, master files and intellectual property belong exclusively to your brand."
      }
    ]
  },
  {
    "categorySlug": "post-produksiyon",
    "categoryTitle": "Post-Production & VFX",
    "slug": "ses-tasarimi-mastering",
    "title": "Sound Design & Audio Mastering",
    "metaTitle": "Sound Design & Audio Mastering - WE24 Agency",
    "metaDescription": "Professional sound design & audio mastering services by WE24 Agency. Delivering high-standard creative, production and technological solutions.",
    "keywords": [
      "sound design & audio mastering",
      "we24 agency istanbul",
      "creative agency",
      "production services"
    ],
    "heroTagline": "Accelerate your brand with world-class sound design & audio mastering solutions.",
    "shortDescription": "We provide comprehensive, high-standard sound design & audio mastering services tailored to elevate brand prestige and drive measurable results.",
    "fullDescription": "WE24 Agency delivers premium sound design & audio mastering services powered by deep industry experience, cutting-edge technology and refined aesthetic craft. We partner with leading global and national brands to create impactful, lasting digital and creative assets.",
    "whyUs": [
      "15+ Years of Specialized Industry Experience",
      "End-to-End In-House Production & Engineering Teams",
      "State-of-the-Art Technical Equipment & Software Stacks",
      "Multilingual Global Delivery Standards",
      "Dedicated Project Management & Agile Communication"
    ],
    "deliverables": [
      "High-Resolution Master Deliverables (4K / Lossless / Vector)",
      "Platform-Optimized Multi-Format Digital Assets",
      "Full Source Files, Brand Guidelines & Documentation",
      "Dedicated Technical Support & Maintenance"
    ],
    "process": [
      "Discovery & Strategy: In-depth analysis of your objectives, audience and technical requirements.",
      "Creative & Technical Execution: Precision production and development by our senior specialists.",
      "Review & Quality Assurance: Rigorous testing, multi-device audits and client feedback iterations.",
      "Final Master Delivery: Seamless handover of all production assets, source codes and master files."
    ],
    "faqs": [
      {
        "question": "How does the Sound Design & Audio Mastering project workflow begin?",
        "answer": "We begin with an in-depth discovery consultation to align on your vision, followed by a detailed scope and delivery timeline proposal."
      },
      {
        "question": "What is the typical delivery timeline?",
        "answer": "Project timelines typically range from 1 to 3 weeks depending on the exact scope, revision rounds and technical requirements."
      },
      {
        "question": "Do we retain full intellectual property ownership?",
        "answer": "Yes, 100% of the final deliverables, master files and intellectual property belong exclusively to your brand."
      }
    ]
  },
  {
    "categorySlug": "fotograf-produksiyon",
    "categoryTitle": "Commercial Photography",
    "slug": "mimari-fotograflama",
    "title": "Architectural & Interior Photography",
    "metaTitle": "Architectural & Interior Photography - WE24 Agency",
    "metaDescription": "Professional architectural & interior photography services by WE24 Agency. Delivering high-standard creative, production and technological solutions.",
    "keywords": [
      "architectural & interior photography",
      "we24 agency istanbul",
      "creative agency",
      "production services"
    ],
    "heroTagline": "Accelerate your brand with world-class architectural & interior photography solutions.",
    "shortDescription": "We provide comprehensive, high-standard architectural & interior photography services tailored to elevate brand prestige and drive measurable results.",
    "fullDescription": "WE24 Agency delivers premium architectural & interior photography services powered by deep industry experience, cutting-edge technology and refined aesthetic craft. We partner with leading global and national brands to create impactful, lasting digital and creative assets.",
    "whyUs": [
      "15+ Years of Specialized Industry Experience",
      "End-to-End In-House Production & Engineering Teams",
      "State-of-the-Art Technical Equipment & Software Stacks",
      "Multilingual Global Delivery Standards",
      "Dedicated Project Management & Agile Communication"
    ],
    "deliverables": [
      "High-Resolution Master Deliverables (4K / Lossless / Vector)",
      "Platform-Optimized Multi-Format Digital Assets",
      "Full Source Files, Brand Guidelines & Documentation",
      "Dedicated Technical Support & Maintenance"
    ],
    "process": [
      "Discovery & Strategy: In-depth analysis of your objectives, audience and technical requirements.",
      "Creative & Technical Execution: Precision production and development by our senior specialists.",
      "Review & Quality Assurance: Rigorous testing, multi-device audits and client feedback iterations.",
      "Final Master Delivery: Seamless handover of all production assets, source codes and master files."
    ],
    "faqs": [
      {
        "question": "How does the Architectural & Interior Photography project workflow begin?",
        "answer": "We begin with an in-depth discovery consultation to align on your vision, followed by a detailed scope and delivery timeline proposal."
      },
      {
        "question": "What is the typical delivery timeline?",
        "answer": "Project timelines typically range from 1 to 3 weeks depending on the exact scope, revision rounds and technical requirements."
      },
      {
        "question": "Do we retain full intellectual property ownership?",
        "answer": "Yes, 100% of the final deliverables, master files and intellectual property belong exclusively to your brand."
      }
    ]
  },
  {
    "categorySlug": "fotograf-produksiyon",
    "categoryTitle": "Commercial Photography",
    "slug": "endustriyel-fotografcilik",
    "title": "Industrial & Factory Photography",
    "metaTitle": "Industrial & Factory Photography - WE24 Agency",
    "metaDescription": "Professional industrial & factory photography services by WE24 Agency. Delivering high-standard creative, production and technological solutions.",
    "keywords": [
      "industrial & factory photography",
      "we24 agency istanbul",
      "creative agency",
      "production services"
    ],
    "heroTagline": "Accelerate your brand with world-class industrial & factory photography solutions.",
    "shortDescription": "We provide comprehensive, high-standard industrial & factory photography services tailored to elevate brand prestige and drive measurable results.",
    "fullDescription": "WE24 Agency delivers premium industrial & factory photography services powered by deep industry experience, cutting-edge technology and refined aesthetic craft. We partner with leading global and national brands to create impactful, lasting digital and creative assets.",
    "whyUs": [
      "15+ Years of Specialized Industry Experience",
      "End-to-End In-House Production & Engineering Teams",
      "State-of-the-Art Technical Equipment & Software Stacks",
      "Multilingual Global Delivery Standards",
      "Dedicated Project Management & Agile Communication"
    ],
    "deliverables": [
      "High-Resolution Master Deliverables (4K / Lossless / Vector)",
      "Platform-Optimized Multi-Format Digital Assets",
      "Full Source Files, Brand Guidelines & Documentation",
      "Dedicated Technical Support & Maintenance"
    ],
    "process": [
      "Discovery & Strategy: In-depth analysis of your objectives, audience and technical requirements.",
      "Creative & Technical Execution: Precision production and development by our senior specialists.",
      "Review & Quality Assurance: Rigorous testing, multi-device audits and client feedback iterations.",
      "Final Master Delivery: Seamless handover of all production assets, source codes and master files."
    ],
    "faqs": [
      {
        "question": "How does the Industrial & Factory Photography project workflow begin?",
        "answer": "We begin with an in-depth discovery consultation to align on your vision, followed by a detailed scope and delivery timeline proposal."
      },
      {
        "question": "What is the typical delivery timeline?",
        "answer": "Project timelines typically range from 1 to 3 weeks depending on the exact scope, revision rounds and technical requirements."
      },
      {
        "question": "Do we retain full intellectual property ownership?",
        "answer": "Yes, 100% of the final deliverables, master files and intellectual property belong exclusively to your brand."
      }
    ]
  },
  {
    "categorySlug": "fotograf-produksiyon",
    "categoryTitle": "Commercial Photography",
    "slug": "urun-fotografciligi",
    "title": "Product & E-Commerce Photography",
    "metaTitle": "Product & E-Commerce Photography - WE24 Agency",
    "metaDescription": "Professional product & e-commerce photography services by WE24 Agency. Delivering high-standard creative, production and technological solutions.",
    "keywords": [
      "product & e-commerce photography",
      "we24 agency istanbul",
      "creative agency",
      "production services"
    ],
    "heroTagline": "Accelerate your brand with world-class product & e-commerce photography solutions.",
    "shortDescription": "We provide comprehensive, high-standard product & e-commerce photography services tailored to elevate brand prestige and drive measurable results.",
    "fullDescription": "WE24 Agency delivers premium product & e-commerce photography services powered by deep industry experience, cutting-edge technology and refined aesthetic craft. We partner with leading global and national brands to create impactful, lasting digital and creative assets.",
    "whyUs": [
      "15+ Years of Specialized Industry Experience",
      "End-to-End In-House Production & Engineering Teams",
      "State-of-the-Art Technical Equipment & Software Stacks",
      "Multilingual Global Delivery Standards",
      "Dedicated Project Management & Agile Communication"
    ],
    "deliverables": [
      "High-Resolution Master Deliverables (4K / Lossless / Vector)",
      "Platform-Optimized Multi-Format Digital Assets",
      "Full Source Files, Brand Guidelines & Documentation",
      "Dedicated Technical Support & Maintenance"
    ],
    "process": [
      "Discovery & Strategy: In-depth analysis of your objectives, audience and technical requirements.",
      "Creative & Technical Execution: Precision production and development by our senior specialists.",
      "Review & Quality Assurance: Rigorous testing, multi-device audits and client feedback iterations.",
      "Final Master Delivery: Seamless handover of all production assets, source codes and master files."
    ],
    "faqs": [
      {
        "question": "How does the Product & E-Commerce Photography project workflow begin?",
        "answer": "We begin with an in-depth discovery consultation to align on your vision, followed by a detailed scope and delivery timeline proposal."
      },
      {
        "question": "What is the typical delivery timeline?",
        "answer": "Project timelines typically range from 1 to 3 weeks depending on the exact scope, revision rounds and technical requirements."
      },
      {
        "question": "Do we retain full intellectual property ownership?",
        "answer": "Yes, 100% of the final deliverables, master files and intellectual property belong exclusively to your brand."
      }
    ]
  },
  {
    "categorySlug": "fotograf-produksiyon",
    "categoryTitle": "Commercial Photography",
    "slug": "model-moda-fotografciligi",
    "title": "Model & Fashion Photography",
    "metaTitle": "Model & Fashion Photography - WE24 Agency",
    "metaDescription": "Professional model & fashion photography services by WE24 Agency. Delivering high-standard creative, production and technological solutions.",
    "keywords": [
      "model & fashion photography",
      "we24 agency istanbul",
      "creative agency",
      "production services"
    ],
    "heroTagline": "Accelerate your brand with world-class model & fashion photography solutions.",
    "shortDescription": "We provide comprehensive, high-standard model & fashion photography services tailored to elevate brand prestige and drive measurable results.",
    "fullDescription": "WE24 Agency delivers premium model & fashion photography services powered by deep industry experience, cutting-edge technology and refined aesthetic craft. We partner with leading global and national brands to create impactful, lasting digital and creative assets.",
    "whyUs": [
      "15+ Years of Specialized Industry Experience",
      "End-to-End In-House Production & Engineering Teams",
      "State-of-the-Art Technical Equipment & Software Stacks",
      "Multilingual Global Delivery Standards",
      "Dedicated Project Management & Agile Communication"
    ],
    "deliverables": [
      "High-Resolution Master Deliverables (4K / Lossless / Vector)",
      "Platform-Optimized Multi-Format Digital Assets",
      "Full Source Files, Brand Guidelines & Documentation",
      "Dedicated Technical Support & Maintenance"
    ],
    "process": [
      "Discovery & Strategy: In-depth analysis of your objectives, audience and technical requirements.",
      "Creative & Technical Execution: Precision production and development by our senior specialists.",
      "Review & Quality Assurance: Rigorous testing, multi-device audits and client feedback iterations.",
      "Final Master Delivery: Seamless handover of all production assets, source codes and master files."
    ],
    "faqs": [
      {
        "question": "How does the Model & Fashion Photography project workflow begin?",
        "answer": "We begin with an in-depth discovery consultation to align on your vision, followed by a detailed scope and delivery timeline proposal."
      },
      {
        "question": "What is the typical delivery timeline?",
        "answer": "Project timelines typically range from 1 to 3 weeks depending on the exact scope, revision rounds and technical requirements."
      },
      {
        "question": "Do we retain full intellectual property ownership?",
        "answer": "Yes, 100% of the final deliverables, master files and intellectual property belong exclusively to your brand."
      }
    ]
  },
  {
    "categorySlug": "fotograf-produksiyon",
    "categoryTitle": "Commercial Photography",
    "slug": "havadan-drone-fotograf",
    "title": "Aerial Drone Photography",
    "metaTitle": "Aerial Drone Photography - WE24 Agency",
    "metaDescription": "Professional aerial drone photography services by WE24 Agency. Delivering high-standard creative, production and technological solutions.",
    "keywords": [
      "aerial drone photography",
      "we24 agency istanbul",
      "creative agency",
      "production services"
    ],
    "heroTagline": "Accelerate your brand with world-class aerial drone photography solutions.",
    "shortDescription": "We provide comprehensive, high-standard aerial drone photography services tailored to elevate brand prestige and drive measurable results.",
    "fullDescription": "WE24 Agency delivers premium aerial drone photography services powered by deep industry experience, cutting-edge technology and refined aesthetic craft. We partner with leading global and national brands to create impactful, lasting digital and creative assets.",
    "whyUs": [
      "15+ Years of Specialized Industry Experience",
      "End-to-End In-House Production & Engineering Teams",
      "State-of-the-Art Technical Equipment & Software Stacks",
      "Multilingual Global Delivery Standards",
      "Dedicated Project Management & Agile Communication"
    ],
    "deliverables": [
      "High-Resolution Master Deliverables (4K / Lossless / Vector)",
      "Platform-Optimized Multi-Format Digital Assets",
      "Full Source Files, Brand Guidelines & Documentation",
      "Dedicated Technical Support & Maintenance"
    ],
    "process": [
      "Discovery & Strategy: In-depth analysis of your objectives, audience and technical requirements.",
      "Creative & Technical Execution: Precision production and development by our senior specialists.",
      "Review & Quality Assurance: Rigorous testing, multi-device audits and client feedback iterations.",
      "Final Master Delivery: Seamless handover of all production assets, source codes and master files."
    ],
    "faqs": [
      {
        "question": "How does the Aerial Drone Photography project workflow begin?",
        "answer": "We begin with an in-depth discovery consultation to align on your vision, followed by a detailed scope and delivery timeline proposal."
      },
      {
        "question": "What is the typical delivery timeline?",
        "answer": "Project timelines typically range from 1 to 3 weeks depending on the exact scope, revision rounds and technical requirements."
      },
      {
        "question": "Do we retain full intellectual property ownership?",
        "answer": "Yes, 100% of the final deliverables, master files and intellectual property belong exclusively to your brand."
      }
    ]
  },
  {
    "categorySlug": "dijital-ve-sosyal-medya",
    "categoryTitle": "Digital & Social Media",
    "slug": "sosyal-medya-hesap-yonetimi",
    "title": "Social Media Account Management",
    "metaTitle": "Social Media Account Management - WE24 Agency",
    "metaDescription": "Professional social media account management services by WE24 Agency. Delivering high-standard creative, production and technological solutions.",
    "keywords": [
      "social media account management",
      "we24 agency istanbul",
      "creative agency",
      "production services"
    ],
    "heroTagline": "Accelerate your brand with world-class social media account management solutions.",
    "shortDescription": "We provide comprehensive, high-standard social media account management services tailored to elevate brand prestige and drive measurable results.",
    "fullDescription": "WE24 Agency delivers premium social media account management services powered by deep industry experience, cutting-edge technology and refined aesthetic craft. We partner with leading global and national brands to create impactful, lasting digital and creative assets.",
    "whyUs": [
      "15+ Years of Specialized Industry Experience",
      "End-to-End In-House Production & Engineering Teams",
      "State-of-the-Art Technical Equipment & Software Stacks",
      "Multilingual Global Delivery Standards",
      "Dedicated Project Management & Agile Communication"
    ],
    "deliverables": [
      "High-Resolution Master Deliverables (4K / Lossless / Vector)",
      "Platform-Optimized Multi-Format Digital Assets",
      "Full Source Files, Brand Guidelines & Documentation",
      "Dedicated Technical Support & Maintenance"
    ],
    "process": [
      "Discovery & Strategy: In-depth analysis of your objectives, audience and technical requirements.",
      "Creative & Technical Execution: Precision production and development by our senior specialists.",
      "Review & Quality Assurance: Rigorous testing, multi-device audits and client feedback iterations.",
      "Final Master Delivery: Seamless handover of all production assets, source codes and master files."
    ],
    "faqs": [
      {
        "question": "How does the Social Media Account Management project workflow begin?",
        "answer": "We begin with an in-depth discovery consultation to align on your vision, followed by a detailed scope and delivery timeline proposal."
      },
      {
        "question": "What is the typical delivery timeline?",
        "answer": "Project timelines typically range from 1 to 3 weeks depending on the exact scope, revision rounds and technical requirements."
      },
      {
        "question": "Do we retain full intellectual property ownership?",
        "answer": "Yes, 100% of the final deliverables, master files and intellectual property belong exclusively to your brand."
      }
    ]
  },
  {
    "categorySlug": "dijital-ve-sosyal-medya",
    "categoryTitle": "Digital & Social Media",
    "slug": "dijital-icerik-uretimi",
    "title": "Dynamic Vertical Video & Reels Production",
    "metaTitle": "Dynamic Vertical Video & Reels Production - WE24 Agency",
    "metaDescription": "Professional dynamic vertical video & reels production services by WE24 Agency. Delivering high-standard creative, production and technological solutions.",
    "keywords": [
      "dynamic vertical video & reels production",
      "we24 agency istanbul",
      "creative agency",
      "production services"
    ],
    "heroTagline": "Accelerate your brand with world-class dynamic vertical video & reels production solutions.",
    "shortDescription": "We provide comprehensive, high-standard dynamic vertical video & reels production services tailored to elevate brand prestige and drive measurable results.",
    "fullDescription": "WE24 Agency delivers premium dynamic vertical video & reels production services powered by deep industry experience, cutting-edge technology and refined aesthetic craft. We partner with leading global and national brands to create impactful, lasting digital and creative assets.",
    "whyUs": [
      "15+ Years of Specialized Industry Experience",
      "End-to-End In-House Production & Engineering Teams",
      "State-of-the-Art Technical Equipment & Software Stacks",
      "Multilingual Global Delivery Standards",
      "Dedicated Project Management & Agile Communication"
    ],
    "deliverables": [
      "High-Resolution Master Deliverables (4K / Lossless / Vector)",
      "Platform-Optimized Multi-Format Digital Assets",
      "Full Source Files, Brand Guidelines & Documentation",
      "Dedicated Technical Support & Maintenance"
    ],
    "process": [
      "Discovery & Strategy: In-depth analysis of your objectives, audience and technical requirements.",
      "Creative & Technical Execution: Precision production and development by our senior specialists.",
      "Review & Quality Assurance: Rigorous testing, multi-device audits and client feedback iterations.",
      "Final Master Delivery: Seamless handover of all production assets, source codes and master files."
    ],
    "faqs": [
      {
        "question": "How does the Dynamic Vertical Video & Reels Production project workflow begin?",
        "answer": "We begin with an in-depth discovery consultation to align on your vision, followed by a detailed scope and delivery timeline proposal."
      },
      {
        "question": "What is the typical delivery timeline?",
        "answer": "Project timelines typically range from 1 to 3 weeks depending on the exact scope, revision rounds and technical requirements."
      },
      {
        "question": "Do we retain full intellectual property ownership?",
        "answer": "Yes, 100% of the final deliverables, master files and intellectual property belong exclusively to your brand."
      }
    ]
  },
  {
    "categorySlug": "dijital-ve-sosyal-medya",
    "categoryTitle": "Digital & Social Media",
    "slug": "sosyal-medya-reklam-yonetimi",
    "title": "Performance Marketing & Social Ads",
    "metaTitle": "Performance Marketing & Social Ads - WE24 Agency",
    "metaDescription": "Professional performance marketing & social ads services by WE24 Agency. Delivering high-standard creative, production and technological solutions.",
    "keywords": [
      "performance marketing & social ads",
      "we24 agency istanbul",
      "creative agency",
      "production services"
    ],
    "heroTagline": "Accelerate your brand with world-class performance marketing & social ads solutions.",
    "shortDescription": "We provide comprehensive, high-standard performance marketing & social ads services tailored to elevate brand prestige and drive measurable results.",
    "fullDescription": "WE24 Agency delivers premium performance marketing & social ads services powered by deep industry experience, cutting-edge technology and refined aesthetic craft. We partner with leading global and national brands to create impactful, lasting digital and creative assets.",
    "whyUs": [
      "15+ Years of Specialized Industry Experience",
      "End-to-End In-House Production & Engineering Teams",
      "State-of-the-Art Technical Equipment & Software Stacks",
      "Multilingual Global Delivery Standards",
      "Dedicated Project Management & Agile Communication"
    ],
    "deliverables": [
      "High-Resolution Master Deliverables (4K / Lossless / Vector)",
      "Platform-Optimized Multi-Format Digital Assets",
      "Full Source Files, Brand Guidelines & Documentation",
      "Dedicated Technical Support & Maintenance"
    ],
    "process": [
      "Discovery & Strategy: In-depth analysis of your objectives, audience and technical requirements.",
      "Creative & Technical Execution: Precision production and development by our senior specialists.",
      "Review & Quality Assurance: Rigorous testing, multi-device audits and client feedback iterations.",
      "Final Master Delivery: Seamless handover of all production assets, source codes and master files."
    ],
    "faqs": [
      {
        "question": "How does the Performance Marketing & Social Ads project workflow begin?",
        "answer": "We begin with an in-depth discovery consultation to align on your vision, followed by a detailed scope and delivery timeline proposal."
      },
      {
        "question": "What is the typical delivery timeline?",
        "answer": "Project timelines typically range from 1 to 3 weeks depending on the exact scope, revision rounds and technical requirements."
      },
      {
        "question": "Do we retain full intellectual property ownership?",
        "answer": "Yes, 100% of the final deliverables, master files and intellectual property belong exclusively to your brand."
      }
    ]
  },
  {
    "categorySlug": "dijital-ve-sosyal-medya",
    "categoryTitle": "Digital & Social Media",
    "slug": "youtube-kanal-yonetimi-seo",
    "title": "YouTube Channel Management & Video SEO",
    "metaTitle": "YouTube Channel Management & Video SEO - WE24 Agency",
    "metaDescription": "Professional youtube channel management & video seo services by WE24 Agency. Delivering high-standard creative, production and technological solutions.",
    "keywords": [
      "youtube channel management & video seo",
      "we24 agency istanbul",
      "creative agency",
      "production services"
    ],
    "heroTagline": "Accelerate your brand with world-class youtube channel management & video seo solutions.",
    "shortDescription": "We provide comprehensive, high-standard youtube channel management & video seo services tailored to elevate brand prestige and drive measurable results.",
    "fullDescription": "WE24 Agency delivers premium youtube channel management & video seo services powered by deep industry experience, cutting-edge technology and refined aesthetic craft. We partner with leading global and national brands to create impactful, lasting digital and creative assets.",
    "whyUs": [
      "15+ Years of Specialized Industry Experience",
      "End-to-End In-House Production & Engineering Teams",
      "State-of-the-Art Technical Equipment & Software Stacks",
      "Multilingual Global Delivery Standards",
      "Dedicated Project Management & Agile Communication"
    ],
    "deliverables": [
      "High-Resolution Master Deliverables (4K / Lossless / Vector)",
      "Platform-Optimized Multi-Format Digital Assets",
      "Full Source Files, Brand Guidelines & Documentation",
      "Dedicated Technical Support & Maintenance"
    ],
    "process": [
      "Discovery & Strategy: In-depth analysis of your objectives, audience and technical requirements.",
      "Creative & Technical Execution: Precision production and development by our senior specialists.",
      "Review & Quality Assurance: Rigorous testing, multi-device audits and client feedback iterations.",
      "Final Master Delivery: Seamless handover of all production assets, source codes and master files."
    ],
    "faqs": [
      {
        "question": "How does the YouTube Channel Management & Video SEO project workflow begin?",
        "answer": "We begin with an in-depth discovery consultation to align on your vision, followed by a detailed scope and delivery timeline proposal."
      },
      {
        "question": "What is the typical delivery timeline?",
        "answer": "Project timelines typically range from 1 to 3 weeks depending on the exact scope, revision rounds and technical requirements."
      },
      {
        "question": "Do we retain full intellectual property ownership?",
        "answer": "Yes, 100% of the final deliverables, master files and intellectual property belong exclusively to your brand."
      }
    ]
  },
  {
    "categorySlug": "tasarim-ve-marka",
    "categoryTitle": "Design & Brand Identity",
    "slug": "kurumsal-kimlik-tasarimi",
    "title": "Corporate Brand Identity & Logo Design",
    "metaTitle": "Corporate Brand Identity & Logo Design - WE24 Agency",
    "metaDescription": "Professional corporate brand identity & logo design services by WE24 Agency. Delivering high-standard creative, production and technological solutions.",
    "keywords": [
      "corporate brand identity & logo design",
      "we24 agency istanbul",
      "creative agency",
      "production services"
    ],
    "heroTagline": "Accelerate your brand with world-class corporate brand identity & logo design solutions.",
    "shortDescription": "We provide comprehensive, high-standard corporate brand identity & logo design services tailored to elevate brand prestige and drive measurable results.",
    "fullDescription": "WE24 Agency delivers premium corporate brand identity & logo design services powered by deep industry experience, cutting-edge technology and refined aesthetic craft. We partner with leading global and national brands to create impactful, lasting digital and creative assets.",
    "whyUs": [
      "15+ Years of Specialized Industry Experience",
      "End-to-End In-House Production & Engineering Teams",
      "State-of-the-Art Technical Equipment & Software Stacks",
      "Multilingual Global Delivery Standards",
      "Dedicated Project Management & Agile Communication"
    ],
    "deliverables": [
      "High-Resolution Master Deliverables (4K / Lossless / Vector)",
      "Platform-Optimized Multi-Format Digital Assets",
      "Full Source Files, Brand Guidelines & Documentation",
      "Dedicated Technical Support & Maintenance"
    ],
    "process": [
      "Discovery & Strategy: In-depth analysis of your objectives, audience and technical requirements.",
      "Creative & Technical Execution: Precision production and development by our senior specialists.",
      "Review & Quality Assurance: Rigorous testing, multi-device audits and client feedback iterations.",
      "Final Master Delivery: Seamless handover of all production assets, source codes and master files."
    ],
    "faqs": [
      {
        "question": "How does the Corporate Brand Identity & Logo Design project workflow begin?",
        "answer": "We begin with an in-depth discovery consultation to align on your vision, followed by a detailed scope and delivery timeline proposal."
      },
      {
        "question": "What is the typical delivery timeline?",
        "answer": "Project timelines typically range from 1 to 3 weeks depending on the exact scope, revision rounds and technical requirements."
      },
      {
        "question": "Do we retain full intellectual property ownership?",
        "answer": "Yes, 100% of the final deliverables, master files and intellectual property belong exclusively to your brand."
      }
    ]
  },
  {
    "categorySlug": "tasarim-ve-marka",
    "categoryTitle": "Design & Brand Identity",
    "slug": "sosyal-medya-tasarimi",
    "title": "Social Media Design Systems",
    "metaTitle": "Social Media Design Systems - WE24 Agency",
    "metaDescription": "Professional social media design systems services by WE24 Agency. Delivering high-standard creative, production and technological solutions.",
    "keywords": [
      "social media design systems",
      "we24 agency istanbul",
      "creative agency",
      "production services"
    ],
    "heroTagline": "Accelerate your brand with world-class social media design systems solutions.",
    "shortDescription": "We provide comprehensive, high-standard social media design systems services tailored to elevate brand prestige and drive measurable results.",
    "fullDescription": "WE24 Agency delivers premium social media design systems services powered by deep industry experience, cutting-edge technology and refined aesthetic craft. We partner with leading global and national brands to create impactful, lasting digital and creative assets.",
    "whyUs": [
      "15+ Years of Specialized Industry Experience",
      "End-to-End In-House Production & Engineering Teams",
      "State-of-the-Art Technical Equipment & Software Stacks",
      "Multilingual Global Delivery Standards",
      "Dedicated Project Management & Agile Communication"
    ],
    "deliverables": [
      "High-Resolution Master Deliverables (4K / Lossless / Vector)",
      "Platform-Optimized Multi-Format Digital Assets",
      "Full Source Files, Brand Guidelines & Documentation",
      "Dedicated Technical Support & Maintenance"
    ],
    "process": [
      "Discovery & Strategy: In-depth analysis of your objectives, audience and technical requirements.",
      "Creative & Technical Execution: Precision production and development by our senior specialists.",
      "Review & Quality Assurance: Rigorous testing, multi-device audits and client feedback iterations.",
      "Final Master Delivery: Seamless handover of all production assets, source codes and master files."
    ],
    "faqs": [
      {
        "question": "How does the Social Media Design Systems project workflow begin?",
        "answer": "We begin with an in-depth discovery consultation to align on your vision, followed by a detailed scope and delivery timeline proposal."
      },
      {
        "question": "What is the typical delivery timeline?",
        "answer": "Project timelines typically range from 1 to 3 weeks depending on the exact scope, revision rounds and technical requirements."
      },
      {
        "question": "Do we retain full intellectual property ownership?",
        "answer": "Yes, 100% of the final deliverables, master files and intellectual property belong exclusively to your brand."
      }
    ]
  },
  {
    "categorySlug": "tasarim-ve-marka",
    "categoryTitle": "Design & Brand Identity",
    "slug": "dergi-sayfa-tasarimi-mizanpaj",
    "title": "Catalog, Magazine & Editorial Layout",
    "metaTitle": "Catalog, Magazine & Editorial Layout - WE24 Agency",
    "metaDescription": "Professional catalog, magazine & editorial layout services by WE24 Agency. Delivering high-standard creative, production and technological solutions.",
    "keywords": [
      "catalog, magazine & editorial layout",
      "we24 agency istanbul",
      "creative agency",
      "production services"
    ],
    "heroTagline": "Accelerate your brand with world-class catalog, magazine & editorial layout solutions.",
    "shortDescription": "We provide comprehensive, high-standard catalog, magazine & editorial layout services tailored to elevate brand prestige and drive measurable results.",
    "fullDescription": "WE24 Agency delivers premium catalog, magazine & editorial layout services powered by deep industry experience, cutting-edge technology and refined aesthetic craft. We partner with leading global and national brands to create impactful, lasting digital and creative assets.",
    "whyUs": [
      "15+ Years of Specialized Industry Experience",
      "End-to-End In-House Production & Engineering Teams",
      "State-of-the-Art Technical Equipment & Software Stacks",
      "Multilingual Global Delivery Standards",
      "Dedicated Project Management & Agile Communication"
    ],
    "deliverables": [
      "High-Resolution Master Deliverables (4K / Lossless / Vector)",
      "Platform-Optimized Multi-Format Digital Assets",
      "Full Source Files, Brand Guidelines & Documentation",
      "Dedicated Technical Support & Maintenance"
    ],
    "process": [
      "Discovery & Strategy: In-depth analysis of your objectives, audience and technical requirements.",
      "Creative & Technical Execution: Precision production and development by our senior specialists.",
      "Review & Quality Assurance: Rigorous testing, multi-device audits and client feedback iterations.",
      "Final Master Delivery: Seamless handover of all production assets, source codes and master files."
    ],
    "faqs": [
      {
        "question": "How does the Catalog, Magazine & Editorial Layout project workflow begin?",
        "answer": "We begin with an in-depth discovery consultation to align on your vision, followed by a detailed scope and delivery timeline proposal."
      },
      {
        "question": "What is the typical delivery timeline?",
        "answer": "Project timelines typically range from 1 to 3 weeks depending on the exact scope, revision rounds and technical requirements."
      },
      {
        "question": "Do we retain full intellectual property ownership?",
        "answer": "Yes, 100% of the final deliverables, master files and intellectual property belong exclusively to your brand."
      }
    ]
  },
  {
    "categorySlug": "tasarim-ve-marka",
    "categoryTitle": "Design & Brand Identity",
    "slug": "ambalaj-tasarimi",
    "title": "Packaging & Product Label Design",
    "metaTitle": "Packaging & Product Label Design - WE24 Agency",
    "metaDescription": "Professional packaging & product label design services by WE24 Agency. Delivering high-standard creative, production and technological solutions.",
    "keywords": [
      "packaging & product label design",
      "we24 agency istanbul",
      "creative agency",
      "production services"
    ],
    "heroTagline": "Accelerate your brand with world-class packaging & product label design solutions.",
    "shortDescription": "We provide comprehensive, high-standard packaging & product label design services tailored to elevate brand prestige and drive measurable results.",
    "fullDescription": "WE24 Agency delivers premium packaging & product label design services powered by deep industry experience, cutting-edge technology and refined aesthetic craft. We partner with leading global and national brands to create impactful, lasting digital and creative assets.",
    "whyUs": [
      "15+ Years of Specialized Industry Experience",
      "End-to-End In-House Production & Engineering Teams",
      "State-of-the-Art Technical Equipment & Software Stacks",
      "Multilingual Global Delivery Standards",
      "Dedicated Project Management & Agile Communication"
    ],
    "deliverables": [
      "High-Resolution Master Deliverables (4K / Lossless / Vector)",
      "Platform-Optimized Multi-Format Digital Assets",
      "Full Source Files, Brand Guidelines & Documentation",
      "Dedicated Technical Support & Maintenance"
    ],
    "process": [
      "Discovery & Strategy: In-depth analysis of your objectives, audience and technical requirements.",
      "Creative & Technical Execution: Precision production and development by our senior specialists.",
      "Review & Quality Assurance: Rigorous testing, multi-device audits and client feedback iterations.",
      "Final Master Delivery: Seamless handover of all production assets, source codes and master files."
    ],
    "faqs": [
      {
        "question": "How does the Packaging & Product Label Design project workflow begin?",
        "answer": "We begin with an in-depth discovery consultation to align on your vision, followed by a detailed scope and delivery timeline proposal."
      },
      {
        "question": "What is the typical delivery timeline?",
        "answer": "Project timelines typically range from 1 to 3 weeks depending on the exact scope, revision rounds and technical requirements."
      },
      {
        "question": "Do we retain full intellectual property ownership?",
        "answer": "Yes, 100% of the final deliverables, master files and intellectual property belong exclusively to your brand."
      }
    ]
  },
  {
    "categorySlug": "web-ve-yazilim",
    "categoryTitle": "Web & Software Engineering",
    "slug": "web-tasarim-yazilim",
    "title": "Corporate Web Design & Development",
    "metaTitle": "Corporate Web Design & Development - WE24 Agency",
    "metaDescription": "Professional corporate web design & development services by WE24 Agency. Delivering high-standard creative, production and technological solutions.",
    "keywords": [
      "corporate web design & development",
      "we24 agency istanbul",
      "creative agency",
      "production services"
    ],
    "heroTagline": "Accelerate your brand with world-class corporate web design & development solutions.",
    "shortDescription": "We provide comprehensive, high-standard corporate web design & development services tailored to elevate brand prestige and drive measurable results.",
    "fullDescription": "WE24 Agency delivers premium corporate web design & development services powered by deep industry experience, cutting-edge technology and refined aesthetic craft. We partner with leading global and national brands to create impactful, lasting digital and creative assets.",
    "whyUs": [
      "15+ Years of Specialized Industry Experience",
      "End-to-End In-House Production & Engineering Teams",
      "State-of-the-Art Technical Equipment & Software Stacks",
      "Multilingual Global Delivery Standards",
      "Dedicated Project Management & Agile Communication"
    ],
    "deliverables": [
      "High-Resolution Master Deliverables (4K / Lossless / Vector)",
      "Platform-Optimized Multi-Format Digital Assets",
      "Full Source Files, Brand Guidelines & Documentation",
      "Dedicated Technical Support & Maintenance"
    ],
    "process": [
      "Discovery & Strategy: In-depth analysis of your objectives, audience and technical requirements.",
      "Creative & Technical Execution: Precision production and development by our senior specialists.",
      "Review & Quality Assurance: Rigorous testing, multi-device audits and client feedback iterations.",
      "Final Master Delivery: Seamless handover of all production assets, source codes and master files."
    ],
    "faqs": [
      {
        "question": "How does the Corporate Web Design & Development project workflow begin?",
        "answer": "We begin with an in-depth discovery consultation to align on your vision, followed by a detailed scope and delivery timeline proposal."
      },
      {
        "question": "What is the typical delivery timeline?",
        "answer": "Project timelines typically range from 1 to 3 weeks depending on the exact scope, revision rounds and technical requirements."
      },
      {
        "question": "Do we retain full intellectual property ownership?",
        "answer": "Yes, 100% of the final deliverables, master files and intellectual property belong exclusively to your brand."
      }
    ]
  },
  {
    "categorySlug": "web-ve-yazilim",
    "categoryTitle": "Web & Software Engineering",
    "slug": "ozel-yazilim-gelistirme",
    "title": "Custom Web Applications & SaaS Development",
    "metaTitle": "Custom Web Applications & SaaS Development - WE24 Agency",
    "metaDescription": "Professional custom web applications & saas development services by WE24 Agency. Delivering high-standard creative, production and technological solutions.",
    "keywords": [
      "custom web applications & saas development",
      "we24 agency istanbul",
      "creative agency",
      "production services"
    ],
    "heroTagline": "Accelerate your brand with world-class custom web applications & saas development solutions.",
    "shortDescription": "We provide comprehensive, high-standard custom web applications & saas development services tailored to elevate brand prestige and drive measurable results.",
    "fullDescription": "WE24 Agency delivers premium custom web applications & saas development services powered by deep industry experience, cutting-edge technology and refined aesthetic craft. We partner with leading global and national brands to create impactful, lasting digital and creative assets.",
    "whyUs": [
      "15+ Years of Specialized Industry Experience",
      "End-to-End In-House Production & Engineering Teams",
      "State-of-the-Art Technical Equipment & Software Stacks",
      "Multilingual Global Delivery Standards",
      "Dedicated Project Management & Agile Communication"
    ],
    "deliverables": [
      "High-Resolution Master Deliverables (4K / Lossless / Vector)",
      "Platform-Optimized Multi-Format Digital Assets",
      "Full Source Files, Brand Guidelines & Documentation",
      "Dedicated Technical Support & Maintenance"
    ],
    "process": [
      "Discovery & Strategy: In-depth analysis of your objectives, audience and technical requirements.",
      "Creative & Technical Execution: Precision production and development by our senior specialists.",
      "Review & Quality Assurance: Rigorous testing, multi-device audits and client feedback iterations.",
      "Final Master Delivery: Seamless handover of all production assets, source codes and master files."
    ],
    "faqs": [
      {
        "question": "How does the Custom Web Applications & SaaS Development project workflow begin?",
        "answer": "We begin with an in-depth discovery consultation to align on your vision, followed by a detailed scope and delivery timeline proposal."
      },
      {
        "question": "What is the typical delivery timeline?",
        "answer": "Project timelines typically range from 1 to 3 weeks depending on the exact scope, revision rounds and technical requirements."
      },
      {
        "question": "Do we retain full intellectual property ownership?",
        "answer": "Yes, 100% of the final deliverables, master files and intellectual property belong exclusively to your brand."
      }
    ]
  },
  {
    "categorySlug": "web-ve-yazilim",
    "categoryTitle": "Web & Software Engineering",
    "slug": "e-ticaret-altyapisi",
    "title": "E-Commerce Platforms & Custom Integrations",
    "metaTitle": "E-Commerce Platforms & Custom Integrations - WE24 Agency",
    "metaDescription": "Professional e-commerce platforms & custom integrations services by WE24 Agency. Delivering high-standard creative, production and technological solutions.",
    "keywords": [
      "e-commerce platforms & custom integrations",
      "we24 agency istanbul",
      "creative agency",
      "production services"
    ],
    "heroTagline": "Accelerate your brand with world-class e-commerce platforms & custom integrations solutions.",
    "shortDescription": "We provide comprehensive, high-standard e-commerce platforms & custom integrations services tailored to elevate brand prestige and drive measurable results.",
    "fullDescription": "WE24 Agency delivers premium e-commerce platforms & custom integrations services powered by deep industry experience, cutting-edge technology and refined aesthetic craft. We partner with leading global and national brands to create impactful, lasting digital and creative assets.",
    "whyUs": [
      "15+ Years of Specialized Industry Experience",
      "End-to-End In-House Production & Engineering Teams",
      "State-of-the-Art Technical Equipment & Software Stacks",
      "Multilingual Global Delivery Standards",
      "Dedicated Project Management & Agile Communication"
    ],
    "deliverables": [
      "High-Resolution Master Deliverables (4K / Lossless / Vector)",
      "Platform-Optimized Multi-Format Digital Assets",
      "Full Source Files, Brand Guidelines & Documentation",
      "Dedicated Technical Support & Maintenance"
    ],
    "process": [
      "Discovery & Strategy: In-depth analysis of your objectives, audience and technical requirements.",
      "Creative & Technical Execution: Precision production and development by our senior specialists.",
      "Review & Quality Assurance: Rigorous testing, multi-device audits and client feedback iterations.",
      "Final Master Delivery: Seamless handover of all production assets, source codes and master files."
    ],
    "faqs": [
      {
        "question": "How does the E-Commerce Platforms & Custom Integrations project workflow begin?",
        "answer": "We begin with an in-depth discovery consultation to align on your vision, followed by a detailed scope and delivery timeline proposal."
      },
      {
        "question": "What is the typical delivery timeline?",
        "answer": "Project timelines typically range from 1 to 3 weeks depending on the exact scope, revision rounds and technical requirements."
      },
      {
        "question": "Do we retain full intellectual property ownership?",
        "answer": "Yes, 100% of the final deliverables, master files and intellectual property belong exclusively to your brand."
      }
    ]
  },
  {
    "categorySlug": "web-ve-yazilim",
    "categoryTitle": "Web & Software Engineering",
    "slug": "mobil-yazilim",
    "title": "Mobile App Development & PWA",
    "metaTitle": "Mobile App Development & PWA - WE24 Agency",
    "metaDescription": "Professional mobile app development & pwa services by WE24 Agency. Delivering high-standard creative, production and technological solutions.",
    "keywords": [
      "mobile app development & pwa",
      "we24 agency istanbul",
      "creative agency",
      "production services"
    ],
    "heroTagline": "Accelerate your brand with world-class mobile app development & pwa solutions.",
    "shortDescription": "We provide comprehensive, high-standard mobile app development & pwa services tailored to elevate brand prestige and drive measurable results.",
    "fullDescription": "WE24 Agency delivers premium mobile app development & pwa services powered by deep industry experience, cutting-edge technology and refined aesthetic craft. We partner with leading global and national brands to create impactful, lasting digital and creative assets.",
    "whyUs": [
      "15+ Years of Specialized Industry Experience",
      "End-to-End In-House Production & Engineering Teams",
      "State-of-the-Art Technical Equipment & Software Stacks",
      "Multilingual Global Delivery Standards",
      "Dedicated Project Management & Agile Communication"
    ],
    "deliverables": [
      "High-Resolution Master Deliverables (4K / Lossless / Vector)",
      "Platform-Optimized Multi-Format Digital Assets",
      "Full Source Files, Brand Guidelines & Documentation",
      "Dedicated Technical Support & Maintenance"
    ],
    "process": [
      "Discovery & Strategy: In-depth analysis of your objectives, audience and technical requirements.",
      "Creative & Technical Execution: Precision production and development by our senior specialists.",
      "Review & Quality Assurance: Rigorous testing, multi-device audits and client feedback iterations.",
      "Final Master Delivery: Seamless handover of all production assets, source codes and master files."
    ],
    "faqs": [
      {
        "question": "How does the Mobile App Development & PWA project workflow begin?",
        "answer": "We begin with an in-depth discovery consultation to align on your vision, followed by a detailed scope and delivery timeline proposal."
      },
      {
        "question": "What is the typical delivery timeline?",
        "answer": "Project timelines typically range from 1 to 3 weeks depending on the exact scope, revision rounds and technical requirements."
      },
      {
        "question": "Do we retain full intellectual property ownership?",
        "answer": "Yes, 100% of the final deliverables, master files and intellectual property belong exclusively to your brand."
      }
    ]
  }
];
